import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { login as loginAPI } from '../utils/api';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Pre-warm the backend so it's ready by the time credentials are submitted
  useEffect(function() {
    api.get('/health').catch(function() {});
  }, []);

  useEffect(function() {
    var handler = function(e) { e.preventDefault(); };
    window.addEventListener('beforeinstallprompt', handler);
    return function() { window.removeEventListener('beforeinstallprompt', handler); };
  }, []);

  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/auth/google', { idToken: credentialResponse.credential });
      login(res.data.token, res.data.user);
      const roleRoutes = { admin: '/dashboard', centreAdmin: '/dashboard', healer: '/healer', patient: '/patient', student: '/student', general: '/general', owner: '/owner', superadmin: '/superadmin' };
      const dest = res.data.user.role === 'healer' && res.data.user.primaryMode === 'solo'
        ? '/solo/healer' : roleRoutes[res.data.user.role];
      const f = location.state?.from;
      const from = f
        ? f.pathname + (f.search || '') + (f.hash || '')
        : dest || '/dashboard';
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Google sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await loginAPI({ email, password });
      login(res.data.token, res.data.user);
      const roleRoutes = { admin: '/dashboard', centreAdmin: '/dashboard', healer: '/healer', patient: '/patient', student: '/student', general: '/general', owner: '/owner', superadmin: '/superadmin' };
      const dest = res.data.user.role === 'healer' && res.data.user.primaryMode === 'solo'
        ? '/solo/healer' : roleRoutes[res.data.user.role];
      const f = location.state?.from;
      const from = f
        ? f.pathname + (f.search || '') + (f.hash || '')
        : dest || '/dashboard';
      navigate(from, { replace: true });
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'RATE_LIMIT_EXCEEDED') {
        setError('Too many login attempts. Please wait a few minutes and try again.');
      } else {
        setError(err.response?.data?.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logoWrap}>
          <img
            src="/logo-dark.png"
            alt="LifeChiAI"
            style={{ width: 320, marginBottom: 32, display: 'block', margin: '0 auto 32px auto' }}
          />
        </div>

        {/* Error */}
        {error && <div style={styles.error}>{error}</div>}

        {/* Google SSO */}
        <div style={styles.googleWrap}>
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => setError('Google sign-in failed. Please try again.')}
            width="100%"
            theme="outline"
            shape="rectangular"
            text="signin_with"
          />
        </div>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '20px 0 16px' }}>
          <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.1)' }}></div>
          <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', fontFamily: "'Raleway', sans-serif", letterSpacing: '1px', textTransform: 'uppercase' }}>or</span>
          <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.1)' }}></div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={styles.field}>
            <label style={styles.label}>Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              placeholder="Enter your email"
              required
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              placeholder="Enter your password"
              required
            />
          </div>
          <button
            type="submit"
            style={loading ? { ...styles.btn, opacity: 0.7 } : styles.btn}
            disabled={loading}
          >
            {loading ? 'Signing In...' : 'Sign In to Portal'}
          </button>
        </form>

        {/* Footer */}
        <div style={styles.footer}>
          LifeChiAI
          <span style={{ margin: '0 8px', opacity: 0.3 }}>·</span>
          <a href="/privacy" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px', textDecoration: 'none' }}>
            Privacy Policy
          </a>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #071e12 0%, #0a1f14 30%, #0c2617 60%, #071e12 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: "'Open Sans', sans-serif"
  },
  card: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '16px',
    padding: '48px 40px',
    width: '100%',
    maxWidth: '420px',
    backdropFilter: 'blur(20px)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
  },
  logoWrap: {
    textAlign: 'center',
    marginBottom: '24px'
  },
  logo: {
    height: '56px',
    margin: '0 auto',
    filter: 'drop-shadow(0 2px 8px rgba(141,198,63,0.3))'
  },
  titleWrap: {
    textAlign: 'center',
    marginBottom: '32px'
  },
  title: {
    fontFamily: "'Raleway', sans-serif",
    fontSize: '22px',
    fontWeight: '800',
    color: '#fff',
    margin: '0 0 8px',
    letterSpacing: '0.5px'
  },
  subtitle: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.4)',
    margin: '0 0 16px',
    letterSpacing: '1.5px',
    textTransform: 'uppercase'
  },
  rule: {
    width: '40px',
    height: '2px',
    background: 'linear-gradient(to right, #00AEEF, #4a8f82, #8DC63F)',
    margin: '0 auto'
  },
  error: {
    background: 'rgba(192,57,43,0.2)',
    border: '1px solid rgba(192,57,43,0.4)',
    borderRadius: '6px',
    padding: '12px 16px',
    color: '#e74c3c',
    fontSize: '13px',
    marginBottom: '20px'
  },
  field: {
    marginBottom: '18px'
  },
  label: {
    display: 'block',
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.45)',
    marginBottom: '7px',
    fontFamily: "'Raleway', sans-serif"
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '5px',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: "'Open Sans', sans-serif"
  },
  btn: {
    width: '100%',
    padding: '13px',
    background: '#4a8f82',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    fontFamily: "'Raleway', sans-serif",
    fontSize: '12px',
    fontWeight: '700',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    cursor: 'pointer',
    marginTop: '8px'
  },
  footer: {
    textAlign: 'center',
    marginTop: '24px',
    fontSize: '11px',
    color: 'rgba(255,255,255,0.25)'
  },
  googleWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '4px',
  }
};

export default Login;